import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import UnifiedCascadingSelect from '../UnifiedCascadingSelect';

/**
 * Step 2 - Treatment Type Component
 * Cascading select that shows only treatment types available for selected cultures
 *
 * @param {Object} props
 * @param {string|null} props.selectedTreatmentType - Selected treatment type ID
 * @param {Function} props.onSelectTreatmentType - Callback when treatment type is selected
 * @param {Object} props.selections - Current selections from all steps
 * @param {Array<string>} props.selections.cultures - Selected culture IDs
 * @param {Object} [props.enrichedData] - Enriched data with cached labels to prevent duplicate API calls
 * @param {Function} [props.onMismatchChange] - Callback when mismatch status changes
 */
const Step2TreatmentType = React.memo(
  ({ selectedTreatmentType, onSelectTreatmentType, selections = {}, enrichedData = {}, onMismatchChange }) => {
    const { t } = useTranslation('riskCalculator');

    // Handle treatment type change - pass option for tags display
    const handleTreatmentTypeChange = useCallback(
      (value, option) => {
        onSelectTreatmentType(value, option);
      },
      [onSelectTreatmentType]
    );

    return (
      <div className="flex flex-col justify-center items-center gap-4 sm:gap-6 mx-auto w-full px-2 sm:px-0">
        <h3 className="text-xl sm:text-2xl lg:text-[30px] font-medium text-center normal-case">
          {t('treatment_title')}
        </h3>
        {/* Treatment Type Cascading Select */}
        <div className="step-select-container">
          <UnifiedCascadingSelect
            type="treatment_type"
            cultures={selections.cultures || []}
            value={selectedTreatmentType}
            onChange={handleTreatmentTypeChange}
            placeholder={t('treatment_select_placeholder')}
            enrichedLabel={enrichedData?.treatmentType?.name}
            onMismatchChange={onMismatchChange}
            allowShowAll
          />
        </div>
      </div>
    );
  }
);

Step2TreatmentType.displayName = 'Step2TreatmentType';

export default Step2TreatmentType;
